import { PLANS_CONFIG } from '~/utils/plans.config';
import starterIcon from '~/assets/starter-icon.svg';
import rocketIcon from '~/assets/rocket-icon.svg';
import proEnterpriseIcon from '~/assets/pro-enterprise-icon.svg';
import starterCheckmarkIcon from '~/assets/starter-checkmark-icon.svg';


interface PlansProps {
  fontClass: string;
}

export default function Plans({ fontClass }: PlansProps) {
  const plans = PLANS_CONFIG.filter(plan => plan.id !== 'basic');

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'basic':
        return undefined; // Will render text "B" instead
      case 'starter':
        return starterIcon;
      case 'growth':
        return rocketIcon;
      case 'enterprise':
        return proEnterpriseIcon;
      default:
        return starterIcon;
    }
  };

  const formatPrice = (price: number) => {
    if (price === -1) return '∞';
    return `$${price.toFixed(2)}`;
  };

  const formatLimit = (limit: number) => {
    if (limit === -1) return 'Unlimited';
    return limit.toLocaleString();
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-10 w-full">
      {plans.map((plan) => (
        <div 
          key={plan.id}
          className="flex-1 bg-white relative rounded-2xl shadow-[0px_3px_8px_0px_rgba(44,44,44,0.06)] p-4 md:p-6"
        >
          <div className="flex flex-col gap-4 md:gap-6">
            {/* Icon and Title */}
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="bg-[rgba(105,108,255,0.12)] w-10 md:w-12 h-10 md:h-12 rounded-lg flex items-center justify-center relative">
                <div className="absolute border border-[rgba(105,108,255,0.24)] border-solid inset-0 pointer-events-none rounded-lg" />
                <div className="overflow-clip relative shrink-0 size-6 md:size-8 flex items-center justify-center">
                  {plan.id === 'basic' ? (
                    <span className="text-[#696cff] font-bold text-2xl md:text-3xl">B</span>
                  ) : (
                    <img src={getPlanIcon(plan.id)} alt={`${plan.name} Icon`} className="block max-w-none size-full" />
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <h4 className={`${fontClass} font-semibold text-[#2C2C2C] text-[18px] md:text-[20px] leading-[normal]`}>
                  {plan.name}
                </h4>
                <p className={`${fontClass} text-[#6A6A6A] text-[14px] leading-[1.5]`}>
                  {plan.id === 'basic' && 'Perfect for small shops and new brands testing gamification.'}
                  {plan.id === 'starter' && 'Ideal for growing stores ready to scale their engagement.'}
                  {plan.id === 'growth' && 'Perfect for established stores with high customer engagement and growth goals.'}
                  {plan.id === 'enterprise' && 'Tailored for high-volume stores requiring advanced features and unlimited scalability.'}
                </p>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center gap-2">
              <span className={`${fontClass} font-bold text-[#696cff] text-[24px] md:text-[28px] leading-[normal] whitespace-pre`}>
                {formatPrice(plan.price)}
              </span>
              <span className={`${fontClass} text-[#999999] text-[14px] leading-[normal]`}>
                {plan.price === -1 ? '' : '/month'}
              </span>
            </div>

            {/* Free Trial / Free to Install Info */}
            <div className="bg-[rgba(105,108,255,0.08)] border border-[rgba(105,108,255,0.2)] rounded-lg p-3">
              <p className={`${fontClass} text-[#696cff] text-[12px] md:text-[14px] font-medium text-center`}>
                {plan.trialDays > 0 && plan.usagePricing
                  ? `${plan.trialDays}-Day Free Trial • Up to ${formatLimit(plan.usagePricing.includedPlays)} plays, then $${plan.usagePricing.overageRate.toFixed(2)}/play`
                  : plan.trialDays > 0
                    ? `${plan.trialDays}-Day Free Trial Included`
                    : 'Cancel anytime'}
              </p>
            </div>

            {/* CTA Button */}
            <a 
              href="https://apps.shopify.com/prizo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${fontClass} w-full h-10 bg-[#696cff] text-white rounded-xl text-[14px] leading-[24px] flex items-center justify-center hover:bg-[#696cff]/90 transition-colors`}
            >
              {plan.id === 'basic' ? 'Install Free' : 'Start Free Trial'}
            </a>

            {/* Divider */}
            <div className="h-0 relative w-full">
              <div className="absolute bottom-[-0.5px] left-0 right-0 top-[-0.5px]">
                <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#E5E7EB] to-transparent" />
              </div>
            </div>

            {/* Features List */}
            <div className="flex flex-col gap-3">
              {/* Key Features */}
              <div className="flex items-center gap-2">
                <div className="overflow-clip relative shrink-0 size-4 md:size-5">
                  <img src={starterCheckmarkIcon} alt="Checkmark" className="block max-w-none size-full" />
                </div>
                <span className={`${fontClass} text-[#6A6A6A] text-[14px] leading-[1.5]`}>
                  {formatLimit(plan.limits.activeGames)} Active Game{plan.limits.activeGames !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="overflow-clip relative shrink-0 size-4 md:size-5">
                  <img src={starterCheckmarkIcon} alt="Checkmark" className="block max-w-none size-full" />
                </div>
                <span className={`${fontClass} text-[#6A6A6A] text-[14px] leading-[1.5]`}>
                  {plan.usagePricing
                    ? `Up to ${formatLimit(plan.usagePricing.includedPlays)} plays/month, then $${plan.usagePricing.overageRate.toFixed(2)}/play`
                    : `${formatLimit(plan.limits.monthlyPlays)} Monthly Plays`}
                </span>
              </div>

              {/* Additional Features */}
              {plan.features.slice(2, Math.min(5, plan.features.length)).filter(feature => feature.included).map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="overflow-clip relative shrink-0 size-4 md:size-5">
                    <img src={starterCheckmarkIcon} alt="Checkmark" className="block max-w-none size-full" />
                  </div>
                  <span className={`${fontClass} text-[#6A6A6A] text-[14px] leading-[1.5]`}>
                    {feature.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
